/**
 * Keeps a PDF/image viewer crash from blanking the whole CRM shell
 * (CRM feedback #39). Shows an overlay with the error and a Close control.
 */
import React from 'react';
import { X } from 'lucide-react';
import './PdfViewerModal.css';

interface PdfViewerErrorBoundaryProps {
  onClose: () => void;
  children: React.ReactNode;
}

interface PdfViewerErrorBoundaryState {
  error: Error | null;
}

export class PdfViewerErrorBoundary extends React.Component<
  PdfViewerErrorBoundaryProps,
  PdfViewerErrorBoundaryState
> {
  state: PdfViewerErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PdfViewerErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[PdfViewerErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="pdf-viewer-modal-overlay" onClick={this.props.onClose} role="presentation">
          <div
            className="pdf-viewer-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Document viewer error"
          >
            <header className="pdf-viewer-header">
              <h2 className="pdf-viewer-title">Couldn’t open document</h2>
              <div className="pdf-viewer-actions">
                <button
                  type="button"
                  className="pdf-viewer-close-btn"
                  onClick={this.props.onClose}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </header>
            <div className="pdf-viewer-body">
              <div className="pdf-viewer-error" role="alert" style={{ margin: 24 }}>
                <span>{this.state.error.message || 'Something went wrong opening this file.'}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
